import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pagination } from '@/components/pagination';
import { PageHeader, Segmented, StatusPill } from '@/components/page';
import { EmptyState, MarkTile, Notice, fmtDate, rowLink } from '@/components/workspace-ui';
import { useAuth, useDebouncedValue, useOrganizations } from '@/hooks';
import { cn } from '@/lib/utils';

type StatusFilter = 'all' | 'active' | 'inactive';
const PAGE_SIZE = 10;

export function RoleTag({ role, superAdmin }: { role?: string; superAdmin?: boolean }) {
  const label = role ?? (superAdmin ? 'super admin' : null);
  if (!label) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="rounded-[5px] border border-border px-1.5 py-0.5 text-[11.5px] text-muted-foreground">
      {label}
    </span>
  );
}

export function OrganizationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.is_super_admin === true;
  const { data: orgs = [], isLoading, isError, refetch } = useOrganizations();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const debounced = useDebouncedValue(search);

  // One organization per account: members can only create their first.
  const canCreate = isSuperAdmin || orgs.length === 0;

  const counts = useMemo(
    () => ({
      all: orgs.length,
      active: orgs.filter((o) => o.is_active).length,
      inactive: orgs.filter((o) => !o.is_active).length,
    }),
    [orgs]
  );

  const filtered = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    return orgs.filter(
      (o) =>
        (status === 'all' || (status === 'active') === o.is_active) &&
        (!q || o.name.toLowerCase().includes(q) || o.slug.includes(q))
    );
  }, [orgs, status, debounced]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const rows = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const clearFilters = () => {
    setSearch('');
    setStatus('all');
    setPage(1);
  };

  const newButton = (
    <Button asChild>
      <Link to="/organizations/new">
        <Plus />
        New organization
      </Link>
    </Button>
  );

  return (
    <div>
      <PageHeader
        title="Organizations"
        description={
          isSuperAdmin
            ? 'Every tenant on this authrz.me instance. Organizations own apps, OAuth clients and member roles.'
            : 'Organizations you belong to. Your role decides what you can change.'
        }
        actions={canCreate ? newButton : undefined}
      />

      {isError && (
        <Notice
          tone="bad"
          title="Couldn’t load organizations"
          className="mb-4"
          action={
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Check your connection and try again.
        </Notice>
      )}

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {isLoading ? (
          <div className="divide-y divide-border" aria-busy="true">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                <Skeleton className="h-8 w-8 rounded-lg" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-40" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            ))}
          </div>
        ) : orgs.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="No organizations yet"
            body="Create an organization to own apps, OAuth clients and member roles."
            action={canCreate ? newButton : undefined}
          />
        ) : (
          <>
            <div className="flex flex-col gap-3 border-b border-border p-3 sm:flex-row sm:items-center sm:justify-between">
              <label className="relative block w-full sm:max-w-xs">
                <span className="sr-only">Search organizations</span>
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search name or slug"
                  autoComplete="off"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9"
                />
              </label>
              <Segmented<StatusFilter>
                value={status}
                onChange={(v) => {
                  setStatus(v);
                  setPage(1);
                }}
                options={[
                  { value: 'all', label: 'All', count: counts.all },
                  { value: 'active', label: 'Active', count: counts.active },
                  { value: 'inactive', label: 'Inactive', count: counts.inactive },
                ]}
              />
            </div>

            {filtered.length === 0 ? (
              <EmptyState
                icon={Search}
                title="No organizations match"
                body="Try a different name or slug, or clear the filters."
                action={
                  <Button variant="secondary" onClick={clearFilters}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Your role</TableHead>
                    <TableHead className="text-right">Members</TableHead>
                    <TableHead className="text-right">Apps</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden sm:table-cell">Created</TableHead>
                    <TableHead className="w-8">
                      <span className="sr-only">Open</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((o) => {
                    const href = `/organizations/${o.id}`;
                    return (
                      <TableRow
                        key={o.id}
                        className={cn(rowLink, !o.is_active && 'opacity-60')}
                        onClick={(e) => {
                          if (!(e.target as HTMLElement).closest('a,button')) navigate(href);
                        }}
                      >
                        <TableCell>
                          <Link to={href} className="flex items-center gap-2.5">
                            <MarkTile name={o.name} />
                            <span className="flex min-w-0 flex-col">
                              <b className="max-w-[260px] truncate font-semibold" title={o.name}>
                                {o.name}
                              </b>
                              <span className="max-w-[260px] truncate font-mono text-xs text-muted-foreground" title={o.slug}>
                                {o.slug}
                              </span>
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <RoleTag role={o.role} superAdmin={isSuperAdmin} />
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{o.member_count ?? '—'}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{o.product_count ?? '—'}</TableCell>
                        <TableCell>
                          <StatusPill tone={o.is_active ? 'ok' : 'neutral'}>
                            {o.is_active ? 'Active' : 'Inactive'}
                          </StatusPill>
                        </TableCell>
                        <TableCell className="hidden whitespace-nowrap font-mono text-xs text-muted-foreground sm:table-cell">
                          {fmtDate(o.created_at)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <ChevronRight className="h-4 w-4" aria-hidden="true" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {filtered.length > 0 && (
              <Pagination
                className="px-4 pb-3"
                pagination={{ page: current, pageSize: PAGE_SIZE, total: filtered.length, totalPages }}
                onPageChange={setPage}
                itemLabel="organizations"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
